import * as React from "react";
import styled from "styled-components";
import { Button, SubtleButton, SubtleIconButton } from "components/button";
import { FileUpload } from "components/file-upload";
import { useMutation, useQuery } from "react-query";
import Axios from "axios";
import { generateThumbnail } from "hooks/thumbnail";
import { MdImage, MdPictureAsPdf, MdMoreVert } from "react-icons/md";
import { useSnackbarContext } from "components/snackbar";
import Popper from "components/react-popper";
import Placeholder from "../../component/placeholder";
import Spinner from "components/spinner";
import DropBox from "views/customer/component/dropBox";
import { useAuthContext } from "contexts/auth";
import { useTranslation } from 'react-i18next';
import { useModalContext } from "components/modal";
import FileGrid from "components/file-grid";

type Props = {
  id: string;
  type: string;
};
export default function RequestFilesTab(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.request.container.files.index";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { id, type } = props;
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const { showSnackbar } = useSnackbarContext();
  const { name } = useAuthContext();

  const files = useQuery(
    ["requestFiles", type, id],
    async () =>
      await Axios.get(
        `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}
    `
      ).then((res) => res.data),
    {
      refetchOnWindowFocus: false,
    }
  );

  const sendFileMutation = async (files: any) => {
    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append("file", files[i]);
      let thumbnail;
      if (files[i].type.includes("image")) {
        thumbnail = await generateThumbnail(canvasRef, files[i], "image");
      } else if (files[i].type === "application/pdf") {
        thumbnail = await generateThumbnail(canvasRef, files[i], "pdf");
      } else {
        throw new Error("no");
      }
  
      formData.append("thumbnail", thumbnail);
      formData.append("type", "RequestFile");
      formData.append("filetype", files[i].type);
      formData.append("filesize", files[i].size);
      formData.append("user", name);

      await Axios.post(
        `
          ${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}
          `,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    }
  };

  const sendFile = useMutation(sendFileMutation, {
    onSuccess: () => {
      files.refetch();
      showSnackbar(tr("File Uploaded!"));
    },
  });

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files) {
      const filesArray = Array.from(files);
      sendFile.mutate(filesArray);
    }
  };


  const handleDropChange = async (file: any) => {
    let fileArr: any = [];
    let push: boolean;
    if (file) {
      // checks new files against existing files, doesn't send file if duplicate, which means a new record is no longer created in the database
      for (let i = 0; i < file.length; i++) {
        // all files are pushed to upload unless a duplicate is found
        push = true;
        for (let o = 0; o < files.data.length; o++) {
          if (file[i].name === files.data[o].Key) {
            push = false;
          }
        }
        if (push) {
          fileArr.push(file[i]);
        }
      }

      if (fileArr[0]) {
        sendFile.mutate(fileArr);
      }
    }
  };

  // const populateFilesGrid =
  //   files.data &&
  //   files.data.map((file: any) => {
  //     return (
  //       <FileGrid
  //         key={file.Key}
  //         {...{ file, id, type }}
  //         refetch={() => files.refetch()}
  //       />
  //     );
  //   });

  return (
    <div className="space-y-4">
      <div className="py-4 flex justify-between items-center border-b">
        <h2>{tr("Files")}</h2>
        <FileUpload
          multiple
          accept="image/*, application/pdf"
          component={
            <Button isLoading={sendFile.isLoading}>{tr("Upload File")}</Button>
          }
          onChange={handleChange}
        />
      </div>
      <div>
        {files.isFetching ? (
          <Spinner size={40} />
        ) : files.data ? (
          <div>
            <DropBox 
              onChange={handleDropChange}
              diffStyle={true}
            /> 
            <FileGrid 
              id={id}
              type={type}
              files={files.data}
              refetch={files.refetch}
            />
          </div>
        ) : (
          <div
            style={{
              display: "inline-flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "baseline",
            }}
          >
            <Placeholder
              type="file"
              header={tr("No files attached")}
              label={tr("Uploaded files will be shown here.")}
            />
            <DropBox onChange={handleDropChange} />
          </div>
        )}
        <canvas style={{ display: "none" }} ref={canvasRef} />
      </div>
    </div>
  );
}

const Con = styled.div`
  display: flex;
  flex-direction: column;
  > div {
    margin-top: 1rem;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(2, 1fr);
  justify-content: center;
`;

const FileGridCon = styled.a`
  border: 1px solid #c0c0c0;
  height: 250px;
  min-height: 250px;
  max-height: 250px;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  > div:first-child {
    flex: 1;
    width: 100%;
    overflow: hidden;
  }
  > div:last-child {
    padding: 0.25em 0.5em;
    flex: nowrap;
    flex: 0 0 auto;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    > svg {
      flex: 0 0 auto;
      font-size: 1.1rem;
      margin-right: 0.5em;
    }
    > span {
      font-weight: 600;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      flex: 1;
    }
  }
`;

// const FileGrid = ({
//   file,
//   id,
//   type,
//   refetch,
// }: {
//   file: any;
//   id: string;
//   type: string;
//   refetch: () => void;
// }) => {
//   const { t } = useTranslation();
//   const tBase = "views.request.container.files.index";
//   const tr = (key: string) => t(`${tBase}.${key}`);
//   const { setModal } = useModalContext();
//   const { showSnackbar } = useSnackbarContext();
//   const fileType = file.Key.includes("pdf") ? "pdf" : "image";

//   const extension = file.Key.split(".").pop() || "";
//   const baseName = file.Key.replace(`.${extension}`, "");

//   const getFile = useMutation(
//     async () =>
//       await Axios.get(
//         `
//     ${
//       process.env.REACT_APP_SERVER_URL
//     }/api/v1/company/files/${type}/${id}/${encodeURIComponent(file.Key)}
//     `
//       ).then((res) => res.data),
//     {
//       onSuccess: (data) => {
//         window.open(data, "_blank") || window.location.replace(data);
//       },
//     }
//   );

//   const deleteFile = useMutation(
//     async () =>
//       await Axios.delete(`
//   ${
//     process.env.REACT_APP_SERVER_URL
//   }/api/v1/company/files/${type}/${id}/${encodeURIComponent(file.Key)}
//   `),
//     {
//       onSuccess: () => {
//         refetch();
//         showSnackbar(tr("File deleted!"));
//       },
//     }
//   );

//   const editFile = useMutation(
//     async ({ oldKey, newKey }: { oldKey: string; newKey: string }) => {
//       if (oldKey === newKey) {
//         setModal();
//         return;
//       };
  
//       const res = await Axios.put(
//         `${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${id}/${encodeURIComponent(oldKey)}`,
//         { newKey }
//       );

  
//       return res.data;
//     },
//     {
//       onSuccess: (data) => {
//        
  
//         setModal();
//         refetch();
//         showSnackbar(tr("File updated!"));
//       },
//       onError: (error) => {
//         console.error("Error updating file", error); 
//         showSnackbar(tr("Error updating file"));
//       },
//     }
//   );
  
//   return (
//     <FileGridCon target="_blank" onClick={() => getFile.mutate()}>
//       <div>
//         <img
//           alt={file.Key}
//           src={file.signedUrl}
//           style={{ width: "100%", objectFit: "cover", height: "100%" }}
//         />
//       </div>
//       <div>
//         {fileType === "pdf" ? (
//           <MdPictureAsPdf style={{ color: "red" }} />
//         ) : (
//           <MdImage style={{ color: "green" }} />
//         )}

//         <span>{file.Key}</span>
//         <Popper
//           clickable
//           placement="bottom-end"
//           reference={
//             <SubtleIconButton size="small">
//               <MdMoreVert />
//             </SubtleIconButton>
//           }
//         >
//           {(popperProps: { disable: () => void }) => (
//             <div className="flex flex-col">
//               <SubtleButton
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   popperProps.disable();
//                   setModal({
//                     component: (
//                       <EditFileModal 
//                         oldKey={file.Key} 
//                         newKey={baseName} 
//                         editFile={editFile.mutate} 
//                         setModal={setModal}
//                         ext={extension}
//                         isLoading={editFile.isLoading}
//                       />
//                     ),
//                     label: tr("Edit File Name"),
//                   });
//                 }}
//               >
//                 {tr("Edit")}
//               </SubtleButton>
//               <SubtleButton
//                 isLoading={deleteFile.isLoading}
//                 onClick={(e) => {
//                   e.stopPropagation();
//                   deleteFile.mutate();
//                 }}
//               >
//                 {tr("Delete")}
//               </SubtleButton>
//             </div>
//           )}
//         </Popper>
//       </div>
//     </FileGridCon>
//   );
// };

// const EditFileModal = ({ oldKey, newKey, editFile, setModal, ext, isLoading }: any) => {
//   const { t } = useTranslation();
//   const tBase = "views.request.container.files.index";
//   const tr = (key: string) => t(`${tBase}.${key}`);
//   const [value, setValue] = React.useState(newKey.replace(`.${ext}`, ""));
//   const [isError, setIsError] = React.useState(false);
//   const { showSnackbar } = useSnackbarContext();

//   const handleSave = () => {
//     if (!value.trim()) {
//       showSnackbar(tr("File name cannot be empty"));
//       setIsError(true);
//       setTimeout(() => setIsError(false), 500); // Remove shake effect after animation
//       return;
//     }
//     const finalName = `${value}.${ext}`; // Ensure correct file name with extension
//    
//     editFile({ oldKey, newKey: finalName });
//   };

//   return (
//     <div className="flex flex-col space-y-4">
//       <div className="flex items-center w-full rounded">
//         <input
//           value={value}
//           onChange={(e) => setValue(e.target.value)}
//           placeholder={oldKey.replace(`.${ext}`, "")}
//           className={`ml-2
//             border border-gray-300 p-1 flex-grow w-full rounded-s 
//            ${isError ? "border-red-500 animate-shake" : " focus:outline-blue-300"}
//           `}
//         />
//         <div className="border border-gray-300 p-1 bg-gray-300 rounded-e">
//           .{ext}
//         </div>
//       </div>

//       <div className="flex justify-end space-x-4">
//         <Button 
//           type="secondary"
//           onClick={() => setModal()}
//         >
//           {tr("Cancel")}
//         </Button>
//         <Button
//           isLoading={isLoading}
//           primary
//           onClick={handleSave}
//         >
//           {tr("Save")}
//         </Button>
//       </div>
//     </div>
//   );
// };